/**
 * Demo component to test FilterPanel integration
 * This component demonstrates the complete filtering workflow
 */

import React, { useState, useMemo } from 'react';
import SimpleFilterPanel from './SimpleFilterPanel';
import { applyFilters, extractFilterOptions } from '@/utils/bulk-analysis-filters';
import { Card } from '@/components/ui/card';

// Mock race data for testing
const mockRaceData = [
  {
    id: '1',
    date: '2024-01-15',
    track: 'Flemington',
    state: 'VIC',
    tips: [
      { odds: 3.5, position: 'T1' },
      { odds: 4.2, position: 'T2' },
      { odds: 2.8, position: 'T3' }
    ],
    horseCount: 12,
    raceNumber: 1
  },
  {
    id: '2',
    date: '2024-01-16',
    track: 'Randwick',
    state: 'NSW',
    tips: [
      { odds: 5.0, position: 'T1' },
      { odds: 3.0, position: 'T2' }
    ],
    horseCount: 8,
    raceNumber: 2
  },
  {
    id: '3',
    date: '2024-01-20',
    track: 'Eagle Farm',
    state: 'QLD',
    tips: [
      { odds: 2.5, position: 'T1' },
      { odds: 6.0, position: 'T2' },
      { odds: 4.5, position: 'T3' },
      { odds: 3.2, position: 'T4' }
    ],
    horseCount: 16,
    raceNumber: 3
  },
  {
    id: '4',
    date: '2024-01-21',
    track: 'Caulfield',
    state: 'VIC',
    tips: [
      { odds: 4.0, position: 'T1' },
      { odds: 7.5, position: 'T2' }
    ],
    horseCount: 10,
    raceNumber: 4
  },
  {
    id: '5',
    date: '2024-01-22',
    track: 'Gold Coast',
    state: 'QLD',
    tips: [
      { odds: 3.8, position: 'T1' },
      { odds: 2.2, position: 'T2' },
      { odds: 5.5, position: 'T3' }
    ],
    horseCount: 14,
    raceNumber: 5
  }
];

const FilterPanelDemo = () => {
  const [filters, setFilters] = useState({
    dateRange: { start: null, end: null },
    daysOfWeek: [],
    states: [],
    tracks: [],
    combinedOddsThreshold: null,
    horseCountRange: { min: null, max: null },
    raceTypes: []
  });

  // Extract available options from mock data
  const availableOptions = useMemo(() => {
    return extractFilterOptions(mockRaceData);
  }, []);

  // Apply filters to get filtered results
  const filteredRaces = useMemo(() => {
    return applyFilters(mockRaceData, filters);
  }, [filters]);

  const handleFiltersChange = (newFilters) => {
    console.log('Filters changed:', newFilters);
    setFilters(newFilters);
  };

  const calculateCombinedOdds = (race) => {
    return race.tips.reduce((sum, tip) => sum + parseFloat(tip.odds), 0);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Filter Panel */}
        <div className="lg:col-span-1">
          <SimpleFilterPanel
            onFiltersChange={handleFiltersChange}
            availableOptions={availableOptions}
            currentFilters={filters}
          />
        </div>

        {/* Results Display */}
        <div className="lg:col-span-2 space-y-4">
          {/* Summary Stats */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Filter Results</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {filteredRaces.length}
                </div>
                <div className="text-sm text-gray-600">
                  Filtered Races
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-600">
                  {mockRaceData.length}
                </div>
                <div className="text-sm text-gray-600">
                  Total Races
                </div>
              </div>
            </div>
          </Card>

          {/* Available Options Display */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Available Filter Options</h3>
            <div className="space-y-3">
              <div>
                <div className="text-sm font-medium mb-1">States:</div>
                <div className="flex flex-wrap gap-1">
                  {availableOptions.states.map(state => (
                    <span key={state} className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                      {state}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium mb-1">Tracks:</div>
                <div className="flex flex-wrap gap-1">
                  {availableOptions.tracks.map(track => (
                    <span key={track} className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                      {track}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium mb-1">Date Range:</div>
                <div className="text-xs text-gray-600">
                  {availableOptions.dateRange.earliest} to {availableOptions.dateRange.latest}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium mb-1">Horse Count Range:</div>
                <div className="text-xs text-gray-600">
                  {availableOptions.horseCountRange.min} to {availableOptions.horseCountRange.max} horses
                </div>
              </div>
            </div>
          </Card>

          {/* Filtered Race Results */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Filtered Races</h3>
            {filteredRaces.length === 0 ? (
              <div className="text-center py-8 text-gray-600">
                No races match the current filters
              </div>
            ) : (
              <div className="space-y-3">
                {filteredRaces.map(race => (
                  <div key={race.id} className="border rounded-lg p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-medium">
                          {race.track} - Race {race.raceNumber}
                        </div>
                        <div className="text-sm text-gray-600">
                          {race.date} • {race.state} • {race.horseCount} horses
                        </div>
                      </div>
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                        Combined Odds: {calculateCombinedOdds(race).toFixed(1)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {race.tips.map(tip => (
                        <span key={tip.position} className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded border">
                          {tip.position}: {tip.odds}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FilterPanelDemo;